import {INPUT_FORM_FIELD_SX, SEARCH_DELAY_IN_MS} from '@hconnect/common/consts'
import {useEquipmentByMainEquipment} from '@hconnect/common/hooks/useEquipmentByMainEquipment'
import {useEquipmentNumberSearchQuery} from '@hconnect/common/hooks/useEquipmentNumberSearchQuery'
import type {EquipmentIdType, EquipmentPlain} from '@hconnect/common/types'
import {areEquipmentsSame, getEquipmentLabel} from '@hconnect/common/utils'
import {Autocomplete, Paper, TextField} from '@mui/material'
import useDebounce from '@react-hook/debounce'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {EquipmentHierarchy} from './EquipmentHierarchy'

const paper = (props) => <Paper elevation={8} style={{minWidth: '350px'}} {...props} />

const emptyArray = []

function getDisplayPath(equipmentPlain: EquipmentPlain) {
  const equipment = equipmentPlain.equipment
  return (
    <span data-test-id={`equipment-search-suggestion-${equipment.id}`}>
      <EquipmentHierarchy equipmentPlain={equipmentPlain} withId={true} />
    </span>
  )
}

type Props = {
  label?: React.ReactNode
  errorText?: React.ReactNode
  onChange: (equipment: EquipmentPlain | undefined) => void
  plantId: string
  value: EquipmentPlain | undefined
  required?: boolean
  disabled?: boolean
  mainEquipmentId?: string
  idType?: EquipmentIdType
}

export const EquipmentSearchHierarchy = (props: Props) => {
  const {t} = useTranslation()
  const [open, setOpen] = React.useState<boolean>(false)

  const [inputValue, setInputValue] = React.useState<string>('')
  const [debouncedSearchText, setDebouncedSearchText] = useDebounce(inputValue, SEARCH_DELAY_IN_MS)

  const mainEquipmentId = props.mainEquipmentId
  const searchQuery = useEquipmentNumberSearchQuery(
    {
      searchTerm: debouncedSearchText,
      plantId: props.plantId,
      idType: props.idType
    },
    {
      enabled: !mainEquipmentId
    }
  )
  const searchMainEquipmentBasedEquipmentQuery = useEquipmentByMainEquipment(
    props.plantId,
    debouncedSearchText,
    mainEquipmentId,
    {
      enabled: !!mainEquipmentId
    }
  )

  const options = mainEquipmentId
    ? (searchMainEquipmentBasedEquipmentQuery.data ?? emptyArray)
    : (searchQuery.data ?? emptyArray)

  const value = props.value?.equipment.text

  useEffect(() => {
    setInputValue(value ?? '')
  }, [value])

  return (
    <Autocomplete
      data-test-id="equipment-search"
      value={props.value ?? null}
      inputValue={inputValue}
      open={open && options.length > 0}
      onOpen={() => {
        setOpen(true)
      }}
      onChange={(event, next, reason) => {
        if (reason === 'clear') {
          setInputValue('')
          setDebouncedSearchText('')
          props.onChange(undefined)
          return
        }
        if (next && typeof next !== 'string') {
          props.onChange(next)
        }
      }}
      onClose={() => {
        setOpen(false)
      }}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input' && newValue !== inputValue) {
          setInputValue(newValue)
          setDebouncedSearchText(newValue)

          if (!newValue && props.value) {
            props.onChange(undefined)
          }
        }
      }}
      options={options}
      getOptionLabel={(plain) => getEquipmentLabel(plain.equipment, false)}
      isOptionEqualToValue={areEquipmentsSame}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            error={!!props.errorText}
            data-test-id="equipment-search-input"
            helperText={props.errorText}
            label={props.label}
            required={props.required}
            placeholder={t('pleaseStartTyping')}
            variant="filled"
            InputProps={{...params.InputProps, ...INPUT_FORM_FIELD_SX}}
          />
        )
      }}
      PaperComponent={paper}
      forcePopupIcon={false}
      renderOption={(props, option) => <li {...props}>{getDisplayPath(option)}</li>}
      // per default the auto-compleat component will use the getOptionLabel,
      // compare it with the given input text and will filter out all that do not match.
      // that is a problem when having sapNumbers were we remove the leading zeros
      // 00000008321003 will be rendered as 8321003 and therefor not match if the input is 0083
      // and will not be presented as an option
      // to avoid this behavior, we override the filterOptions function to force it to compare on object level
      filterOptions={(options) => options}
      disabled={props.disabled}
    />
  )
}
